"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, MapPin, Building2, ShieldCheck, Landmark, ArrowRight, DraftingCompass } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const roadmapPhases = [{
  id: "phase-1",
  title: "Residency Alignment",
  description: "Supporting sustainable international mobility and regulatory clarity.",
  icon: MapPin,
  points: ["Succession awareness", "Rights Coordination", "Freedom of Movement Strategy", "Jurisdictional Compliance"]
}, {
  id: "phase-2",
  title: "Business Organization",
  description: "Advisory on organizing international business activities in a practical & sustainable manner.",
  icon: Building2,
  points: ["Cross-Border Entity Formation", "Lifestyle alignment", "Banking Infrastructure", "Tax-Efficient Structuring"]
}, {
  id: "phase-3",
  title: "Long-Term Planning",
  description: "Guidance on maintaining continuity and resilience across jurisdictions.",
  icon: ShieldCheck,
  points: ["International Tax Optimization", "Asset Protection Trusts", "Multi-jurisdiction coordination", "Regulatory Risk Management"]
}, {
  id: "phase-4",
  title: "Sovereign Legacy",
  description: "Ensuring your international positioning evolves with your long-term objectives.",
  icon: Landmark,
  points: ["Estate & Succession Planning", "Multi-Generational Strategy", "Lifestyle Design", "Strategic periodic review"]
}];
export default function GlobalResilienceRoadmap() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="global-resilience-roadmap" ref={sectionRef} className="relative w-full py-24 overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1552867/img/world_map_watermark_faint.png')] bg-cover bg-center opacity-[0.03] dark:opacity-[0.05]" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        {}
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="inline-flex items-center justify-center gap-2 px-4 py-1.5 mb-6 text-xs font-mono tracking-widest uppercase border rounded-full border-accent/30 text-accent bg-accent/5">
            <DraftingCompass className="size-3" />
            The Blueprint
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium mb-6 tracking-tight text-foreground">
            Global Resilience Roadmap
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">A structured progression from international positioning to long-term strategic stability. <br />We design clear, compliant frameworks that align residency, business organization, and long-term planning objectives.</p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="hidden md:block absolute top-1/2 left-0 w-full h-px bg-border -translate-y-1/2 z-0" />

          {}
          <div className="flex flex-col md:flex-row md:overflow-x-auto md:snap-x md:snap-mandatory gap-6 md:gap-8 pb-8 md:px-4 -mx-4 md:mx-0 px-4 scrollbar-hide">
            {roadmapPhases.map((phase, index) => {
            const Icon = phase.icon;
            const delay = index * 150;
            return <div key={phase.id} data-index={index} className={`flex-shrink-0 w-full md:w-[380px] snap-center transition-all duration-700`} style={{
              transitionDelay: `${isVisible ? delay : 0}ms`,
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)'
            }}>
                  <Card className="h-full bg-card/50 backdrop-blur-md border-border/60 hover:border-accent/40 transition-all duration-500 shadow-sm hover:shadow-lg group">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center justify-center size-14 rounded-sm bg-accent/10 text-accent group-hover:bg-accent group-hover:text-accent-foreground transition-all duration-500">
                          <Icon className="size-7" />
                        </div>
                        <span className="text-[10px] font-mono text-muted-foreground/40 font-bold tracking-[0.2em]">
                          PHASE 0{index + 1}
                        </span>
                      </div>
                      <CardTitle className="text-2xl font-serif font-medium tracking-tight">{phase.title}</CardTitle>
                      <CardDescription className="text-base mt-3 min-h-[48px] leading-relaxed">
                        {phase.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="w-full h-px bg-border/30 mb-8" />
                      <ul className="space-y-4">
                        {phase.points.map((point, i) => <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground/90">
                            <Check className="size-4 text-accent shrink-0 mt-0.5" />
                            <span className="leading-tight">{point}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className={`mt-16 text-center transition-all duration-700 delay-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-xl px-10 py-7 text-base rounded-sm">
            <Link to="/ContactPage">
              Start Your Roadmap <ArrowRight className="ml-2 size-5" />
            </Link>
          </Button>
          <p className="mt-6 text-sm font-medium tracking-wide text-muted-foreground/80 uppercase">
            Every journey begins with a clear strategy.
          </p>
        </div>
      </div>
    </section>;
}